import {
  Box,
  Divider,
  GlobalStyles,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import SchoolPrintHeader2 from "app/components/common/SchoolPrintHeader2";
import { selectSelectedSchool } from "app/modules/schools/selectors";
import { DOB_in_Word, FormatFullName } from "app/utils/helper";
import dayjs from "dayjs";
import React from "react";
import { useSelector } from "react-redux";

const TcTemplate2 = ({
  data,
  color,
  printRef,
  showAddress = false,
  showWatermark = false,
  showRegNumber = true,
  showBookNumber = true,
  showAdmissionNumber = true,
  showSrNumber = true,
  showPenNumber = true,
  showSchoolNo = true,
  showAffiliationNumber = true,
  showEmail = true,
  showContactNo = true,
  showWebsite = true,
}) => {
  const selectedSchool = useSelector(selectSelectedSchool);
  const labelSx = {
    fontWeight: 600,
    fontSize: "12px",
    lineHeight: 1.2,
  };
  const subLabelSx = {
    fontWeight: 600,
    fontSize: "12px",
    lineHeight: 1.2,
    pl: "1.4em",
  };
  const valueSx = {
    fontWeight: 600,
    fontSize: "12px",
    lineHeight: 1.2,
    textTransform: "uppercase",
  };

  return (
    <>
      <Box
        ref={printRef}
        sx={{
          width: "100%",
          maxWidth: "202mm",
          mx: "auto",
          border: "1px solid #000000",
          backgroundColor: color,
          boxSizing: "border-box",
          p: 0.75,
          display: "flex",
          flexDirection: "column",
          position: "relative",
        }}
      >
        {showWatermark && selectedSchool?.schoolLogo && (
          <Box
            component="img"
            src={selectedSchool.schoolLogo}
            alt="school logo watermark"
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "500px",
              height: "500px",
              objectFit: "contain",
              opacity: 0.15,
              pointerEvents: "none",
              zIndex: 0,
            }}
          />
        )}
        <SchoolPrintHeader2
          showAffiliationNumber={showAffiliationNumber}
          showEmail={showEmail}
          showContactNo={showContactNo}
          showWebsite={showWebsite}
        />
        <Divider sx={{ backgroundColor: "#000000", my: 0.15 }} />

        <Stack sx={{ backgroundColor: "black" }}>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "12px",
              textAlign: "center",
              py: 0.15,
              color: "#ffffff",
              lineHeight: 1.05,
            }}
          >
            स्थानांतरण प्रमाण पत्र / Transfer Certificate{" "}
          </Typography>
        </Stack>

        <Box sx={{ px: 1, flex: 1, display: "flex", flexDirection: "column" }}>
          <Grid container spacing={0.4} sx={{ px: 0.2, mt: 0.1 }}>
            {showSchoolNo && (
              <Grid item xs={3}>
                <Typography sx={labelSx}>
                  विद्यालय संख्या/School No. :{" "}
                  {selectedSchool?.schoolRegisterNumber
                    ? selectedSchool?.schoolRegisterNumber
                    : ".............."}
                </Typography>
              </Grid>
            )}
            {showBookNumber && (
              <Grid item xs={3}>
                <Typography sx={labelSx}>
                  पुस्तक संख्या/Book No. :{" "}
                  {data?.bookNumber ? data?.bookNumber : ".."}
                </Typography>
              </Grid>
            )}
            {showSrNumber && (
              <Grid item xs={3}>
                <Typography sx={labelSx}>
                  क्रम संख्या/SR No. : {data?.studentProfile?.srNumber ?? "..."}
                </Typography>
              </Grid>
            )}
            {showAdmissionNumber && (
              <Grid item xs={3}>
                <Typography sx={labelSx}>
                  प्रवेश संख्या/Admission No. :{" "}
                  {data?.studentProfile?.admissionNumber ?? ".."}
                </Typography>
              </Grid>
            )}
          </Grid>

          <Grid container spacing={0.4} sx={{ px: 0.2 }}>
            {showAffiliationNumber && (
              <Grid item xs={3}>
                <Typography sx={labelSx}>
                  Affiliation Number :{" "}
                  {selectedSchool?.affiliationNumber
                    ? selectedSchool?.affiliationNumber
                    : ".............."}
                </Typography>
              </Grid>
            )}
            <Grid item xs={3}>
              <Typography sx={labelSx}>Renewed up to Date: </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography sx={labelSx}>
                Status of School/Senior Secondary :{" "}
              </Typography>
            </Grid>
          </Grid>

          <Grid container spacing={0.4} sx={{ px: 0.2, mb: 0.1 }}>
            {showRegNumber && (
              <Grid item xs={8}>
                <Typography sx={labelSx}>
                  Registration No. of the candidate ( in case of Class -{" "}
                  <span style={{ fontWeight: 600 }}>IX</span> to{" "}
                  <span style={{ fontWeight: 600 }}>XII</span> ) :{" "}
                  {data?.studentProfile?.registrationNumber ?? ".."}
                </Typography>
              </Grid>
            )}
            {showPenNumber && (
              <Grid item xs={4}>
                <Typography sx={{ ...labelSx, textAlign: "center" }}>
                  PEN Number: {data?.studentProfile?.penNumber_center ?? "----"}
                </Typography>
              </Grid>
            )}
          </Grid>

          <Divider sx={{ backgroundColor: "#000000", my: 0.15 }} />

          <Box
            sx={{
              px: 1,
              display: "flex",
              flexDirection: "column",
              gap: "10px",
            }}
          >
            {/* Name of the Pupil  */}
            <Grid container spacing={0.3}>
              <Grid item xs={7}>
                <Typography sx={labelSx}>
                  1. विद्यार्थी का नाम / Name of the pupil
                </Typography>
              </Grid>
              <Grid item xs={5}>
                <Typography sx={valueSx}>
                  :{" "}
                  {FormatFullName(
                    data?.studentProfile?.firstName,
                    data?.studentProfile?.middleName,
                    data?.studentProfile?.lastName,
                  )}
                </Typography>
              </Grid>
            </Grid>
            {/* Mother's Name */}
            <Grid container spacing={0.3}>
              <Grid item xs={7}>
                <Typography sx={labelSx}>
                  2. माता का नाम / Mother's Name
                </Typography>
              </Grid>
              <Grid item xs={5}>
                <Typography sx={valueSx}>
                  :{" "}
                  {FormatFullName(
                    data?.studentProfile?.motherInfo?.firstName,
                    data?.studentProfile?.motherInfo?.middleName,
                    data?.studentProfile?.motherInfo?.lastName,
                  )}
                </Typography>
              </Grid>
            </Grid>
            {/* Father's Name */}
            <Grid container spacing={0.3}>
              <Grid item xs={7}>
                <Typography sx={labelSx}>
                  3. पिता का नाम / Father's Name
                </Typography>
              </Grid>
              <Grid item xs={5}>
                <Typography sx={valueSx}>
                  :{" "}
                  {FormatFullName(
                    data?.studentProfile?.fatherInfo?.firstName,
                    data?.studentProfile?.fatherInfo?.middleName,
                    data?.studentProfile?.fatherInfo?.lastName,
                  )}{" "}
                </Typography>
              </Grid>
            </Grid>
            {/* Nationality */}
            <Grid container spacing={0.3}>
              <Grid item xs={7}>
                <Typography sx={labelSx}>
                  4. राष्ट्रीयता / Nationality
                </Typography>
              </Grid>
              <Grid item xs={5}>
                <Typography sx={valueSx}>: INDIAN</Typography>
              </Grid>
            </Grid>
            {/* Category */}
            <Grid container spacing={0.3}>
              <Grid item xs={7}>
                <Typography sx={labelSx}>
                  5. क्या छात्र अनुo जाति/जoजाo/पिछडा वर्ग से संबंधित है
                </Typography>
                <Typography sx={subLabelSx}>
                  Whether the pupil belongs to SC/ST/OBC Category
                </Typography>
              </Grid>
              <Grid item xs={5}>
                <Typography sx={valueSx}>
                  : {data?.studentProfile?.category}
                </Typography>
              </Grid>
            </Grid>
            {/* Date of Admission */}
            <Grid container spacing={0.3}>
              <Grid item xs={7}>
                <Typography sx={labelSx}>
                  6. विद्यालय में प्रथम प्रवेश की तिथि और कक्षा
                </Typography>
                <Typography sx={subLabelSx}>
                  Date of first admission in the school with class
                </Typography>
              </Grid>
              <Grid item xs={5}>
                <Typography sx={valueSx}>
                  :{" "}
                  {dayjs(data?.firstAdmissionDetail?.admissionDate).format(
                    "DD-MM-YYYY",
                  )}
                  , {data?.firstAdmissionDetail?.className}-
                  {data?.firstAdmissionDetail?.stream}-
                  {data?.firstAdmissionDetail?.section}
                </Typography>
              </Grid>
            </Grid>
            {/* Date of Birth */}
            <Grid container spacing={0.3}>
              <Grid item xs={7}>
                <Typography sx={labelSx}>
                  7. प्रवेश पुस्तिका के अनुसार जन्मतिथि (अंको में/In Figure)
                </Typography>
                <Typography sx={subLabelSx}>
                  Date of Birth according to the Admission Register (शब्दों
                  में/in words)
                </Typography>
              </Grid>
              <Grid item xs={5}>
                <Typography sx={valueSx}>
                  : {dayjs(data?.studentProfile?.DOB).format("DD-MM-YYYY")}
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: "12px",
                    textTransform: "uppercase",
                    lineHeight: 1.2,
                  }}
                >
                  : {DOB_in_Word(data?.studentProfile?.DOB)}
                </Typography>
              </Grid>
            </Grid>
            {/* Failed Status */}
            <Grid container spacing={0.3}>
              <Grid item xs={7}>
                <Typography sx={labelSx}>
                  8. क्या विद्यार्थी का परीक्षा परिणाम अनुत्तीर्ण है?
                </Typography>
                <Typography sx={subLabelSx}>
                  Whether the student is failed?
                </Typography>
              </Grid>
              <Grid item xs={5}>
                <Typography sx={valueSx}>
                  : {data?.lastClassDetail?.failCount === 0 ? "No" : "Yes"}
                </Typography>
              </Grid>
            </Grid>
            {/* Subject Offered */}
            <Grid container spacing={0.3}>
              <Grid item xs={7}>
                <Typography sx={labelSx}>
                  9. प्रस्तावित विषय / Subjects Offered
                </Typography>
              </Grid>
              <Grid item xs={5}>
                <Typography sx={valueSx}>
                  : {data?.lastClassDetail?.subjects?.join(", ")}
                </Typography>
              </Grid>
            </Grid>
            {/* last studied */}
            <Grid container spacing={0.3}>
              <Grid item xs={7}>
                <Typography sx={labelSx}>
                  10. पिछली कक्षा जिसमें विद्यार्थी अध्ययनरत था : (शब्दों में)
                </Typography>
                <Typography sx={subLabelSx}>
                  Class in which the pupil last studied (in words).
                </Typography>
              </Grid>
              <Grid item xs={5}>
                <Typography sx={valueSx}>
                  : {data?.lastClassDetail?.className}
                </Typography>
              </Grid>
            </Grid>
            {/*school Board */}
            <Grid container spacing={0.3}>
              <Grid item xs={7}>
                <Typography sx={labelSx}>
                  11.पिछले विद्यालय/बोर्ड परीक्षा एवं परिणाम
                </Typography>
                <Typography sx={subLabelSx}>
                  School/Board Annual examination last taken with result.
                </Typography>
              </Grid>
              <Grid item xs={5}>
                <Typography sx={valueSx}>
                  : {data?.lastClassDetail?.result || "N/A"}
                </Typography>
              </Grid>
            </Grid>
            {/*promote */}
            <Grid container spacing={0.3}>
              <Grid item xs={7}>
                <Typography sx={labelSx}>
                  12.क्या विद्यार्थी उच्च कक्षा में पदोन्नति का अधिकारी है?
                </Typography>
                <Typography sx={subLabelSx}>
                  Whether qualified for promotion to the next higher class?
                </Typography>
              </Grid>
              <Grid item xs={5}>
                <Typography sx={valueSx}>
                  : {data?.ableToPromote ? "Yes" : "No"}
                </Typography>
              </Grid>
            </Grid>
            {/*all dues cleared */}
            <Grid container spacing={0.3}>
              <Grid item xs={7}>
                <Typography sx={labelSx}>
                  13.क्या विद्यार्थी ने विद्यालय के सभी देय राशि का भुगतान कर
                  दिया है?
                </Typography>
                <Typography sx={subLabelSx}>
                  Whether the pupil has paid all dues to the school?
                </Typography>
              </Grid>
              <Grid item xs={5}>
                <Typography sx={valueSx}>
                  : {data?.isDuesCleared ? "Yes" : "No"}
                </Typography>
              </Grid>
            </Grid>
            {/*slip */}
            <Grid container spacing={0.3}>
              <Grid item xs={7}>
                <Typography sx={labelSx}>
                  14.क्या विद्यार्थी को शुल्क में कोई रियायत मिल रही थी, यदि
                  हाँ, तो उसकी प्रकृति
                </Typography>
                <Typography sx={subLabelSx}>
                  Whether the pupil was in receipt of any fee concession, if so,
                  the nature of such concession
                </Typography>
              </Grid>
              <Grid item xs={5}>
                <Typography sx={valueSx}>
                  : {data?.availedFeeConcession}{" "}
                  {data?.availedFeeConcession === "Yes" &&
                    `(${data?.availedFeeConcessionRemarks})`}
                </Typography>
              </Grid>
            </Grid>
            {/*NCC */}
            <Grid container spacing={0.3}>
              <Grid item xs={7}>
                <Typography sx={labelSx}>
                  15. क्या विद्यार्थी एनसीसी कैडेट/स्काउट/गर्ल गाइड है (विवरण
                  दें)
                </Typography>
                <Typography sx={subLabelSx}>
                  Whether the pupil is NCC Cadet/Boy Scout/Girl Guide (give
                  detail)
                </Typography>
              </Grid>
              <Grid item xs={5}>
                <Typography sx={valueSx}>
                  : {data?.participated_in_NCC}
                </Typography>
              </Grid>
            </Grid>
            {/*Date */}
            <Grid container spacing={0.3}>
              <Grid item xs={7}>
                <Typography sx={labelSx}>
                  16. विद्यालय से विद्यार्थी का नाम काटे जाने की तिथि
                </Typography>
                <Typography sx={subLabelSx}>
                  Date on which the pupil's name was struck off the rolls of the
                  school
                </Typography>
              </Grid>
              <Grid item xs={5}>
                <Typography sx={valueSx}>
                  :{" "}
                  {dayjs(data?.lastClassDetail?.relievingDate).format(
                    "DD-MM-YYYY",
                  )}
                </Typography>
              </Grid>
            </Grid>
            {/*Reason for leaving */}
            <Grid container spacing={0.3}>
              <Grid item xs={7}>
                <Typography sx={labelSx}>
                  17.विद्यालय छोड़ने का कारण / Reason for leaving the school
                </Typography>
              </Grid>
              <Grid item xs={5}>
                <Typography sx={valueSx}>
                  : {data?.lastClassDetail?.relievingReason}
                </Typography>
              </Grid>
            </Grid>
            {/*total working days */}
            <Grid container spacing={0.3}>
              <Grid item xs={7}>
                <Typography sx={labelSx}>
                  18. अंतिम तिथि तक उपस्तिथियो की कुल संख्या
                </Typography>
                <Typography sx={subLabelSx}>
                  No. of meetings up to date.
                </Typography>
              </Grid>
              <Grid item xs={5}>
                <Typography sx={valueSx}>: {data?.totalWorkingDays}</Typography>
              </Grid>
            </Grid>
            {/*total working days */}
            <Grid container spacing={0.3}>
              <Grid item xs={7}>
                <Typography sx={labelSx}>
                  19. विद्यार्थी की विद्यालय दिवसो की कुल उपस्तिथि / No. of
                  School
                </Typography>
                <Typography sx={subLabelSx}>
                  days of pupil attended date.
                </Typography>
              </Grid>
              <Grid item xs={5}>
                <Typography sx={valueSx}>: {data?.totalPresent}</Typography>
              </Grid>
            </Grid>
            {/*general conduct */}
            <Grid container spacing={0.3}>
              <Grid item xs={7}>
                <Typography sx={labelSx}>
                  20.सामान्य आचरण / General Conduct
                </Typography>
              </Grid>
              <Grid item xs={5}>
                <Typography sx={valueSx}>: {data?.generalConduct}</Typography>
              </Grid>
            </Grid>
            {/*school category*/}
            <Grid container spacing={0.5}>
              <Grid item xs={7}>
                <Typography sx={labelSx}>
                  21. क्या विद्यालय सरकारी/अल्पसंख्यक/स्वतंत्र श्रेणी के अंतर्गत
                  है?
                </Typography>
                <Typography sx={subLabelSx}>
                  Whether School is under Govt./Minority/Independent Category
                </Typography>
              </Grid>
              <Grid item xs={5}>
                <Typography sx={valueSx}>:</Typography>
              </Grid>
            </Grid>
            {/*Remarks*/}
            <Grid container spacing={0.5}>
              <Grid item xs={7}>
                <Typography sx={labelSx}>
                  22.कोई अन्य टिप्पणी / Any Other Remarks
                </Typography>
              </Grid>
              <Grid item xs={5}>
                <Typography sx={valueSx}>: {data?.remarks}</Typography>
              </Grid>
            </Grid>
            {/*Certificate*/}
            <Grid container spacing={0.5}>
              <Grid item xs={7}>
                <Typography sx={labelSx}>
                  23.प्रमाणपत्र जारी करने की तिथि / Date of issue of certificate
                </Typography>
              </Grid>
              <Grid item xs={5}>
                <Typography sx={valueSx}>
                  : {dayjs(data?.certificateIssueDate).format("DD-MM-YYYY")}
                </Typography>
              </Grid>
            </Grid>
            {showAddress && (
              <Grid container spacing={0.3}>
                <Grid item xs={7}>
                  <Typography sx={labelSx}>24. पता / Address</Typography>
                </Grid>
                <Grid item xs={5}>
                  <Typography sx={valueSx}>
                    :{" "}
                    {data?.studentProfile?.permanentAddress?.address ??
                      data?.address ??
                      ""}
                    ,{" "}
                    {data?.studentProfile?.permanentAddress?.dist ??
                      data?.dist ??
                      ""}
                    ,{" "}
                    {data?.studentProfile?.permanentAddress?.state ??
                      data?.state ??
                      ""}
                    ,{" "}
                    {data?.studentProfile?.permanentAddress?.pinCode ??
                      data?.pinCode ??
                      ""}
                  </Typography>
                </Grid>
              </Grid>
            )}
          </Box>

          {/* signature */}
          <Stack
            direction="row"
            justifyContent="space-between"
            sx={{ mt: 7, pt: 0.3, pb: 0.1 }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "12px",
                textAlign: "center",
                width: "32%",
                lineHeight: 1.2,
              }}
            >
              तैयारकर्ता / Prepared By <br />( Name & Designation )
            </Typography>

            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "12px",
                textAlign: "center",
                width: "32%",
                lineHeight: 1.2,
              }}
            >
              जांचकर्ता / Checked By <br />( Name & Designation )
            </Typography>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "12px",
                textAlign: "center",
                width: "32%",
                lineHeight: 1.2,
              }}
            >
              हo प्राचार्य / कार्यालय{"  "}
              <span
                style={{
                  fontWeight: 700,
                  fontSize: "12px",
                }}
              >
                मोहर
              </span>{" "}
              <br />( Sign of Principal with Official Seal )
            </Typography>
          </Stack>
        </Box>
      </Box>
    </>
  );
};

export default TcTemplate2;
